import type { CommandHandler } from '../types';

// Row shape for project-related grids; keeps common display fields typed
type ProjectRow = Record<string, unknown> & {
  Id?: number;
  ProjectName?: string;
  Name?: string;
  Title?: string;
  Caption?: string;
  [key: string]: unknown;
};

/**
 * Handler for 'refresh' command
 * Refreshes grid data from the server
 */
export const refreshCommandHandler: CommandHandler = (_command, context) => {
  const { onRefresh } = context;

  onRefresh();

  return {
    success: true,
    message: 'Grid refreshed',
    shouldRefresh: true,
  };
};

/**
 * Handler for 'resetfilters' command
 * Resets all active filters to the default state
 */
export const resetFiltersCommandHandler: CommandHandler = (_command, context) => {
  const { onResetFilters, onRefresh } = context;

  if (onResetFilters) {
    // Reset all filters, sorting, and pagination
    onResetFilters();
    return {
      success: true,
      shouldRefresh: false, // onResetFilters already triggers refresh via state change
    };
  }

  // Fallback: just refresh if no reset handler available
  onRefresh();
  return {
    success: true,
    message: 'Grid refreshed',
    shouldRefresh: true,
  };
};

/**
 * Opens details pages for all selected child tasks in new tabs.
 * Uses ChildTaskId field from the row data, not the row's own Id.
 */
export const openChildTaskDetailsCommandHandler: CommandHandler = (command, context) => {
  const { selectedRows, gridData, displayData } = context;

  if (!selectedRows || selectedRows.size === 0) {
    return { success: false, error: 'Please choose at least one row' };
  }

  if (!command.settings) {
    return { success: false, error: 'Child task details URL is not configured' };
  }

  const rowsSource: Record<string, unknown>[] =
    gridData && gridData.length > 0 ? gridData : (displayData ?? []);

  let openedCount = 0;

  for (const selectedId of selectedRows) {
    if (selectedId.startsWith('temp_')) continue;

    const row = rowsSource.find((r) => String(r['Id']) === String(selectedId));
    const rawId = row?.['ChildTaskId'];
    const childTaskId = rawId != null && rawId !== '' ? String(rawId) : '';

    if (!childTaskId) {
      console.warn(`openChildTaskDetails: ChildTaskId not found for row Id=${selectedId}`, {
        row,
        availableKeys: row ? Object.keys(row) : [],
      });
      continue;
    }

    window.open(`${command.settings}${childTaskId}`, '_blank', 'noopener,noreferrer');
    openedCount++;
  }

  if (openedCount === 0) {
    return { success: false, error: 'Could not resolve ChildTaskId for selected row(s)' };
  }

  return { success: true };
};

/**
 * Generic navigation handler for commands with 'action' property
 * Handles both relative paths and absolute URLs
 */
export const navigationCommandHandler: CommandHandler = (command, context) => {
  const { navigate } = context;

  if (!command.action) {
    return {
      success: false,
      error: 'No navigation action specified',
    };
  }

  const action = command.action;

  // Check if it's an absolute URL
  if (action.startsWith('http://') || action.startsWith('https://')) {
    window.open(action, '_blank', 'noopener,noreferrer');
    return {
      success: true,
      message: 'Opening in new tab',
    };
  }

  // Navigate to a relative path
  navigate(action);

  return {
    success: true,
    message: 'Navigating...',
  };
};

/**
 * Handler for opening a project board from the project list
 * Mirrors the legacy openProjectBoard action by requiring a selected project and a configured URL.
 */
export const openProjectBoardCommandHandler: CommandHandler = (command, context) => {
  const { selectedRows, gridData, displayData } = context;

  if (!command.settings) {
    return {
      success: false,
      error: 'Project board URL is not configured',
    };
  }

  if (!selectedRows || selectedRows.size === 0) {
    return {
      success: false,
      error: 'Please choose at least one row',
    };
  }

  if (selectedRows.size === 1) {
    const projectId = Number(selectedRows.values().next().value!);

    if (!Number.isFinite(projectId) || projectId <= 0) {
      return {
        success: false,
        error: 'Selected project is invalid',
      };
    }

    window.location.assign(`${command.settings ?? ''}?projectId=${projectId}`);
    return {
      success: true,
      message: 'Opening project board',
    };
  }

  // Multiple selection — build modal payload with buttons for each selected row
  const rowsSource: Record<string, unknown>[] =
    (gridData && gridData.length > 0 ? gridData : displayData) || [];

  // Build candidate list from selection, resolving display names from row when available
  const candidates = Array.from(selectedRows).map((selectedProjectId) => {
    const row = rowsSource.find((r) => {
      const id = (r as ProjectRow).Id;
      return id != null && Number(id) === Number(selectedProjectId);
    }) as ProjectRow | undefined;

    const idForParam = row ? (row.Id ?? selectedProjectId) : selectedProjectId;
    const nameFromRow = row ? row.ProjectName : undefined;
    const displayName = nameFromRow ? nameFromRow : `${idForParam}`;

    return { idForParam, displayName };
  });

  // Validate candidates — filter out empty or clearly-invalid ids (mirrors single-select checks)
  const validCandidates = candidates.filter((c) => {
    if (!c.idForParam) return false;
    const asNumber = Number(c.idForParam);
    return Number.isFinite(asNumber) && asNumber > 0;
  });

  if (validCandidates.length === 0) {
    return {
      success: false,
      error: 'No valid projects selected',
    };
  }

  const buttons = validCandidates.map((c) => ({
    id: `${c.idForParam}`,
    caption: `Open ${c.displayName}`,
    projectId: `${c.idForParam}`,
  }));

  return {
    success: true,
    modal: {
      title: command.caption ?? 'Open project board',
      baseUrl: command.settings ?? '',
      buttons,
    },
  };
};

/**
 * Default fallback handler for unknown commands
 * Logs warning and attempts to use command.action if available
 */
export const defaultCommandHandler: CommandHandler = (command, context) => {
  console.warn(`No handler registered for command alias: "${command.alias}"`, command);

  // If command has an action property, try to navigate
  if (command.action) {
    return navigationCommandHandler(command, context);
  }

  // If command has pathToJs, it's a custom JS command from old frontend
  if (command.pathToJs) {
    console.warn(
      `Command "${command.alias}" has pathToJs="${command.pathToJs}" but custom JS execution is not yet implemented`
    );
  }

  return {
    success: false,
    error: `Command "${command.alias}" is not yet implemented`,
  };
};
